import ClearIcon from "@mui/icons-material/Clear";
import {
  Autocomplete,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";

const controlSx = {
  width: { xs: "100%", sm: 170 },
  height: 38,
  borderRadius: "33px",
  bgcolor: "neutral.100",
  color: "primary.900",
  fontSize: 13,
  fontWeight: 700,
  boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "transparent" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "transparent" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "secondary.500",
  },
  "& .MuiSelect-select": { display: "flex", alignItems: "center", py: 0 },
};

function FilterSelect({
  value,
  placeholder,
  disabled,
  options,
  onChange,
}) {
  const selectedLabel = options.find(
    (option) => String(option.value) === String(value)
  )?.label;
  const safeValue = selectedLabel ? value : "";

  return (
    <Select
      value={safeValue}
      onChange={(event) => onChange(event.target.value)}
      displayEmpty
      size="small"
      disabled={disabled}
      IconComponent={safeValue ? () => null : undefined}
      inputProps={{ "aria-label": `Filtro ${placeholder.toLowerCase()}` }}
      sx={{
        ...controlSx,
        "& .MuiSelect-select": {
          ...controlSx["& .MuiSelect-select"],
          pr: safeValue ? "32px !important" : undefined,
        },
      }}
      renderValue={() => selectedLabel || placeholder}
      endAdornment={
        safeValue ? (
          <InputAdornment
            position="end"
            sx={{ position: "absolute", right: 8, color: "primary.900" }}
          >
            <IconButton
              size="small"
              aria-label={`Limpiar filtro ${placeholder.toLowerCase()}`}
              onMouseDown={(event) => event.preventDefault()}
              onClick={(event) => {
                event.stopPropagation();
                onChange("");
              }}
              sx={{ width: 24, height: 24, opacity: 0.7 }}
            >
              <ClearIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </InputAdornment>
        ) : null
      }
    >
      <MenuItem value="">{`Todos: ${placeholder.toLowerCase()}`}</MenuItem>
      {options.map((option) => (
        <MenuItem key={option.value} value={String(option.value)}>
          {option.label}
        </MenuItem>
      ))}
    </Select>
  );
}

function DateFilter({ label, value, disabled, onChange }) {
  return (
    <TextField
      type="date"
      label={label}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      disabled={disabled}
      size="small"
      InputLabelProps={{ shrink: true }}
      inputProps={{ "aria-label": `Fecha ${label.toLowerCase()}` }}
      sx={{
        width: { xs: "100%", sm: 165 },
        "& .MuiOutlinedInput-root": {
          height: 38,
          borderRadius: "33px",
          bgcolor: "neutral.100",
          color: "primary.900",
          fontSize: 13,
          fontWeight: 700,
          boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)",
          "& fieldset": { borderColor: "transparent" },
          "&:hover fieldset": { borderColor: "transparent" },
          "&.Mui-focused fieldset": { borderColor: "secondary.500" },
        },
        "& .MuiInputLabel-root": {
          color: "primary.900",
          fontSize: 12,
          fontWeight: 700,
        },
      }}
    />
  );
}

export default function PurchaseFilters({
  selectedSupplier,
  status,
  dateFrom,
  dateTo,
  suppliers,
  suppliersLoading,
  disabled,
  onSupplierChange,
  onSupplierSearchChange,
  onChange,
}) {
  const statusOptions = [
    { value: "PENDING", label: "Pendiente" },
    { value: "APPROVED", label: "Aprobado" },
  ];

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1.25}
      useFlexGap
      sx={{ flexWrap: "wrap", alignItems: { xs: "stretch", sm: "center" } }}
    >
      <Autocomplete
        value={selectedSupplier}
        options={suppliers}
        loading={suppliersLoading}
        disabled={disabled}
        clearText="Limpiar proveedor"
        openText="Mostrar proveedores"
        closeText="Ocultar proveedores"
        filterOptions={(options) => options}
        getOptionLabel={(option) => option?.name || ""}
        isOptionEqualToValue={(option, value) =>
          Number(option.id) === Number(value.id)
        }
        noOptionsText="No se encontraron proveedores"
        loadingText="Buscando proveedores..."
        onChange={(_, option) => onSupplierChange(option)}
        onInputChange={(_, value, reason) =>
          onSupplierSearchChange(value, reason)
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Proveedor"
            size="small"
            inputProps={{
              ...params.inputProps,
              maxLength: 200,
              "aria-label": "Filtrar por proveedor",
            }}
            InputProps={{
              ...params.InputProps,
              startAdornment: suppliersLoading ? (
                <InputAdornment position="start">
                  <CircularProgress size={15} />
                </InputAdornment>
              ) : null,
            }}
            sx={{
              width: { xs: "100%", sm: 220 },
              "& .MuiOutlinedInput-root": {
                ...controlSx,
                width: "100%",
                pr: "34px !important",
              },
            }}
          />
        )}
      />
      <FilterSelect
        value={status}
        placeholder="Estado"
        disabled={disabled}
        options={statusOptions}
        onChange={(value) => onChange("status", value)}
      />
      <DateFilter
        label="Desde"
        value={dateFrom}
        disabled={disabled}
        onChange={(value) => onChange("dateFrom", value)}
      />
      <DateFilter
        label="Hasta"
        value={dateTo}
        disabled={disabled}
        onChange={(value) => onChange("dateTo", value)}
      />
    </Stack>
  );
}
